import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Select } from '@/components/v2/ui/Select'
import { Input } from '@/components/v2/ui/Input'
import { Button } from '@/components/v2/ui/Button'
import type { SegmentCondition, SegmentField } from './segmentPresets'

const BOOL_FIELDS: ReadonlySet<SegmentField> = new Set(['is_active', 'has_image'])
const FIELD_OPTIONS: SegmentField[] = [
  'is_active', 'has_image',
  'quantity_30d_min', 'revenue_30d_min', 'quantity_90d_min', 'revenue_90d_min',
]

interface SegmentRuleRowProps {
  condition: SegmentCondition
  onChange: (c: SegmentCondition) => void
  onRemove: () => void
}

/** Single editable condition row. Field determines op (eq=bool, gte=numeric). */
export function SegmentRuleRow({ condition, onChange, onRemove }: SegmentRuleRowProps) {
  const { t } = useTranslation()
  const { field, value } = condition
  const isBool = BOOL_FIELDS.has(field)

  function handleFieldChange(newField: string) {
    const f = newField as SegmentField
    onChange(BOOL_FIELDS.has(f) ? { field: f, op: 'eq', value: false } : { field: f, op: 'gte', value: 0 })
  }

  const fieldOptions = FIELD_OPTIONS.map((f) => ({
    value: f,
    label: t(`productSegments.fields.${f}`),
  }))

  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 min-w-0">
        <Select
          data-testid="rule-field"
          options={fieldOptions}
          value={field}
          onValueChange={handleFieldChange}
          aria-label={t('productSegments.builder.fieldLabel')}
        />
      </div>

      <span className="text-sm text-neutral-500 dark:text-neutral-400 shrink-0 px-1">
        {isBool ? t('productSegments.builder.op.eq') : t('productSegments.builder.op.gte')}
      </span>

      {isBool ? (
        <Button
          data-testid="rule-bool"
          variant="outline"
          size="sm"
          onClick={() => onChange({ field, op: 'eq', value: !value })}
          aria-label={t('productSegments.builder.toggleBool')}
          aria-pressed={!!value}
        >
          {value ? t('productSegments.builder.boolTrue') : t('productSegments.builder.boolFalse')}
        </Button>
      ) : (
        <div className="w-28 shrink-0">
          <Input
            data-testid="rule-value"
            type="number"
            min={0}
            value={Number(value)}
            // Number('') === 0; kein NaN möglich (Browser normalisiert Teileingabe zu '')
            onChange={(e) => onChange({ field, op: 'gte', value: Number(e.target.value) })}
            aria-label={t('productSegments.builder.valueLabel')}
          />
        </div>
      )}

      <Button
        data-testid="rule-remove"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label={t('productSegments.builder.removeCondition')}
      >
        <X size={16} aria-hidden="true" />
      </Button>
    </div>
  )
}

SegmentRuleRow.displayName = 'SegmentRuleRow'
